import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { opsApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';

const STATUS_LABELS = { PENDING_PAYMENT: 'Pending', confirmed: 'Confirmed', processing: 'Processing', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled', refunded: 'Refunded' };
const STATUS_COLORS = { PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800', processing: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800', refunded: 'bg-gray-100 text-gray-700' };
const PAYMENT_COLORS = { PAID: 'text-green-600', OPEN: 'text-yellow-600', FAILED: 'text-red-600', CANCELLED: 'text-red-500', EXPIRED: 'text-gray-500' };

export default function OpsOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    opsApi.orders({ status: filter, limit: 100 }).then(r => {
      if (!cancelled) { setOrders(r.data.orders); setTotal(r.data.total); }
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter]);

  const statuses = ['', 'PENDING_PAYMENT', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

  return (
    <div data-testid="ops-orders" className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-gray-900">Orders Control</h1><p className="text-sm text-gray-500 mt-1">{total} orders total</p></div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {statuses.map(s => (
          <button key={s} data-testid={`ops-filter-${s || 'all'}`} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filter === s ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'}`}>
            {s ? STATUS_LABELS[s] || s : 'All'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={`orders-skeleton-${i}`} className="h-12 bg-gray-100 rounded-lg animate-pulse" />)}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Order</th>
                <th className="px-4 py-3 text-left font-semibold">Customer</th>
                <th className="px-4 py-3 text-center font-semibold">B2B</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-center font-semibold">Payment</th>
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map(o => (
                <tr key={o._id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3 font-mono font-semibold text-gray-900">#{o.orderNumber}</td>
                  <td className="px-4 py-3 text-gray-600">{o.user?.name || o.user?.email || o.guestEmail || <span className="text-gray-400">Guest</span>}</td>
                  <td className="px-4 py-3 text-center">
                    {o.isB2B ? <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{o.b2bCompany || 'B2B'}</span> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{formatCurrency(o.totalAmount)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-600'}`}>{STATUS_LABELS[o.status] || o.status}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs font-semibold ${PAYMENT_COLORS[o.paymentStatus] || 'text-gray-400'}`}>{o.paymentStatus || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{o.created_at ? new Date(o.created_at).toLocaleDateString('nl-BE', { dateStyle: 'medium' }) : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/ops/orders/${o._id}`} className="text-xs text-blue-600 font-semibold hover:underline">Detail</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!orders.length && <p className="text-center py-12 text-gray-400 text-sm">No orders found</p>}
        </div>
      )}
    </div>
  );
}
